// src/components/AuthBackground.jsx
// إطار موحّد لصفحات الدخول والتسجيل والاستعادة — يُدار من src/config/loginBackground.js

import React, { useState } from 'react';
import {
  Box, IconButton, Menu, MenuItem, Tooltip, Typography, Divider, ListSubheader, useMediaQuery,
} from '@mui/material';
import { keyframes } from '@mui/system';
import {
  PaletteOutlined, Check, CheckRounded, InsightsOutlined,
  VerticalSplitOutlined, CropSquareOutlined, ViewQuiltOutlined,
} from '@mui/icons-material';
import { LOGIN_BACKGROUND, BACKGROUND_PRESETS } from '../config/loginBackground';

const PRESET_KEY = 'loginBackground';
const LAYOUT_KEY = 'loginLayout';

// ───────────────────────── الحركة ─────────────────────────
const drift = keyframes`
  0%   { transform: translate3d(0, 0, 0) scale(1); }
  50%  { transform: translate3d(4%, -3%, 0) scale(1.08); }
  100% { transform: translate3d(-3%, 4%, 0) scale(0.96); }
`;
const floatY = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-10px); }
`;

const GLOW_SPOTS = [
  { top: '-18%', left: '-12%', size: '62%' },
  { bottom: '-22%', right: '-14%', size: '68%' },
  { top: '30%', right: '18%', size: '40%' },
];

// تحبّب خفيف يزيل تدرّج الألوان المتقطع ويعطي ملمسًا فاخرًا
const NOISE = `url("data:image/svg+xml,${encodeURIComponent(
  "<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='3' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(#n)'/></svg>"
)}")`;

// ───────────────────────── قراءة الإعدادات ─────────────────────────
function readSaved(key) {
  if (!LOGIN_BACKGROUND.allowSwitcher) return null;
  try { return localStorage.getItem(key); } catch { return null; }
}

function resolvePreset() {
  const saved = readSaved(PRESET_KEY);
  if (saved && BACKGROUND_PRESETS[saved]) return saved;
  const env = process.env.REACT_APP_LOGIN_BG;
  if (env && BACKGROUND_PRESETS[env]) return env;
  return BACKGROUND_PRESETS[LOGIN_BACKGROUND.preset] ? LOGIN_BACKGROUND.preset : 'aurora';
}

function resolveLayout() {
  const valid = ['framed', 'split', 'center'];
  const saved = readSaved(LAYOUT_KEY);
  if (valid.includes(saved)) return saved;
  const env = process.env.REACT_APP_LOGIN_LAYOUT;
  if (valid.includes(env)) return env;
  return valid.includes(LOGIN_BACKGROUND.layout) ? LOGIN_BACKGROUND.layout : 'framed';
}

function patternStyle(pattern, color) {
  switch (pattern) {
    case 'dots':
      return { backgroundImage: `radial-gradient(${color} 1px, transparent 1.4px)`, backgroundSize: '20px 20px' };
    case 'grid':
      return {
        backgroundImage: `linear-gradient(${color} 1px, transparent 1px), linear-gradient(90deg, ${color} 1px, transparent 1px)`,
        backgroundSize: '56px 56px',
      };
    case 'islamic': {
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='72' height='72'><g fill='none' stroke='${color}' stroke-width='1'><rect x='20' y='20' width='32' height='32'/><rect x='20' y='20' width='32' height='32' transform='rotate(45 36 36)'/><circle cx='36' cy='36' r='7'/><path d='M0 36h13M59 36h13M36 0v13M36 59v13'/></g></svg>`;
      return { backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(svg)}")`, backgroundSize: '72px 72px' };
    }
    default:
      return null;
  }
}

// ───────────────────────── طبقات الخلفية ─────────────────────────
function BackgroundLayers({ preset, spotlight, fixed }) {
  const pattern = patternStyle(preset.pattern, preset.patternColor);
  const overlay = LOGIN_BACKGROUND.imageOverlay ?? 0.6;

  return (
    <Box aria-hidden sx={{
      position: fixed ? 'fixed' : 'absolute', inset: 0, overflow: 'hidden',
      pointerEvents: 'none', zIndex: 0, background: preset.base, transition: 'background .6s ease',
    }}>
      {preset.image && (
        <Box sx={{
          position: 'absolute', inset: 0, backgroundSize: 'cover', backgroundPosition: 'center',
          backgroundImage: `linear-gradient(rgba(11,16,35,${overlay}), rgba(11,16,35,${overlay})), url("${LOGIN_BACKGROUND.imageUrl}")`,
        }} />
      )}

      {(preset.glows || preset.blobs || []).slice(0, 3).map((color, i) => {
        const s = GLOW_SPOTS[i];
        return (
          <Box key={i} sx={{
            position: 'absolute', top: s.top, left: s.left, right: s.right, bottom: s.bottom,
            width: s.size, aspectRatio: '1 / 1', borderRadius: '50%',
            background: `radial-gradient(circle at center, ${color} 0%, transparent 68%)`,
            filter: 'blur(60px)',
            animation: LOGIN_BACKGROUND.animated ? `${drift} ${26 + i * 7}s ease-in-out ${-i * 6}s infinite alternate` : 'none',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }} />
        );
      })}

      {pattern && (
        <Box sx={{
          position: 'absolute', inset: 0, ...pattern,
          WebkitMaskImage: 'radial-gradient(ellipse 70% 60% at 50% 45%, #000 10%, transparent 80%)',
          maskImage: 'radial-gradient(ellipse 70% 60% at 50% 45%, #000 10%, transparent 80%)',
        }} />
      )}

      {spotlight && (
        <Box sx={{
          position: 'absolute', inset: 0,
          background: preset.dark
            ? 'radial-gradient(ellipse 520px 460px at 50% 50%, rgba(255,255,255,.07), transparent 70%)'
            : 'radial-gradient(ellipse 520px 460px at 50% 50%, rgba(255,255,255,.8), transparent 70%)',
        }} />
      )}

      <Box sx={{ position: 'absolute', inset: 0, backgroundImage: NOISE, opacity: preset.dark ? 0.07 : 0.04, mixBlendMode: 'overlay' }} />

      {preset.dark && (
        <Box sx={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,.35) 100%)' }} />
      )}
    </Box>
  );
}

// ───────────────────────── بطاقة الإحصائية الزخرفية ─────────────────────────
function StatsCard({ preset }) {
  const d = preset.dark;
  return (
    <Box sx={{
      width: 300, p: 2.5, borderRadius: '20px',
      bgcolor: d ? 'rgba(255,255,255,.07)' : 'rgba(255,255,255,.75)',
      border: `1px solid ${d ? 'rgba(255,255,255,.12)' : 'rgba(15,23,42,.08)'}`,
      backdropFilter: 'blur(14px)',
      boxShadow: d ? '0 30px 60px -20px rgba(0,0,0,.5)' : '0 30px 60px -25px rgba(15,23,42,.25)',
      animation: LOGIN_BACKGROUND.animated ? `${floatY} 7s ease-in-out infinite` : 'none',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, opacity: 0.7 }}>مبيعات اليوم</Typography>
        <Box sx={{ px: 1, py: 0.25, borderRadius: '999px', fontSize: 12, fontWeight: 800, color: '#10b981', bgcolor: 'rgba(16,185,129,.14)' }} dir="ltr">
          +12.4%
        </Box>
      </Box>
      <Typography sx={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.5 }}>
        48,250 <Typography component="span" sx={{ fontSize: 14, fontWeight: 600, opacity: 0.6 }}>ر.س</Typography>
      </Typography>
      <Box component="svg" viewBox="0 0 220 56" sx={{ width: '100%', height: 56, mt: 1, display: 'block' }}>
        <defs>
          <linearGradient id="authSpark" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#34d399" stopOpacity=".35" />
            <stop offset="100%" stopColor="#34d399" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M0 44 L22 40 L44 46 L66 34 L88 37 L110 26 L132 30 L154 18 L176 22 L198 10 L220 6 L220 56 L0 56 Z" fill="url(#authSpark)" />
        <path d="M0 44 L22 40 L44 46 L66 34 L88 37 L110 26 L132 30 L154 18 L176 22 L198 10 L220 6" fill="none" stroke="#34d399" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </Box>
    </Box>
  );
}

// ───────────────────────── لوحة الهوية ─────────────────────────
function BrandPanel({ preset, compact }) {
  const b = LOGIN_BACKGROUND.brand || {};
  const fg = preset.dark ? '#fff' : '#0f172a';
  const muted = preset.dark ? 'rgba(255,255,255,.72)' : '#475569';

  return (
    <Box sx={{
      position: 'relative', zIndex: 1, height: '100%', color: fg,
      display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      p: compact ? { md: 4, lg: 5 } : { md: 5, lg: 7 }, gap: compact ? 3 : 4,
      '& h2': compact ? { fontSize: { md: '24px !important', lg: '30px !important' } } : {},
    }}>
      {/* الشعار */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: `linear-gradient(135deg, ${preset.accent} 0%, #10b981 100%)`,
          boxShadow: `0 10px 30px -8px ${preset.accent}`,
        }}>
          <InsightsOutlined sx={{ color: '#fff', fontSize: 24 }} />
        </Box>
        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: -0.3 }}>{b.name}</Typography>
      </Box>

      {/* العنوان والمميزات */}
      <Box sx={{ maxWidth: 480 }}>
        <Typography component="h2" sx={{
          fontSize: { md: 32, lg: 40 }, fontWeight: 800, lineHeight: 1.35, whiteSpace: 'pre-line', letterSpacing: -0.5,
        }}>
          {b.tagline}
        </Typography>
        {b.description && (
          <Typography sx={{ mt: 2, fontSize: 16, lineHeight: 1.9, color: muted }}>{b.description}</Typography>
        )}
        {b.features?.length > 0 && (
          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 4, display: 'grid', gap: 1.75 }}>
            {b.features.map(f => (
              <Box component="li" key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                  width: 26, height: 26, borderRadius: '8px', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: preset.dark ? 'rgba(255,255,255,.08)' : 'rgba(79,70,229,.08)',
                  border: `1px solid ${preset.dark ? 'rgba(255,255,255,.12)' : 'rgba(79,70,229,.15)'}`,
                }}>
                  <CheckRounded sx={{ fontSize: 16, color: preset.accent }} />
                </Box>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: preset.dark ? 'rgba(255,255,255,.88)' : '#1e293b' }}>{f}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* البطاقة والتذييل */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 3, flexWrap: 'wrap' }}>
        {!compact && (
          <Typography sx={{ fontSize: 13, color: muted, opacity: 0.8 }}>
            © {new Date().getFullYear()} {b.name}
          </Typography>
        )}
        {b.showStatsCard && (
          <Box sx={{ display: { md: 'none', lg: 'block' }, ...(compact && { mr: 'auto' }) }}>
            <StatsCard preset={preset} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ───────────────────────── اللوحة الجانبية: صورتك الدعائية أو التصميم المولّد ─────────────────────────
function Banner({ preset, compact }) {
  if (LOGIN_BACKGROUND.bannerImage) {
    return (
      <Box
        role="img"
        aria-label={LOGIN_BACKGROUND.brand?.name}
        sx={{
          position: 'absolute', inset: 0,
          backgroundImage: `url("${LOGIN_BACKGROUND.bannerImage}")`,
          backgroundSize: 'cover', backgroundPosition: 'center',
        }}
      />
    );
  }
  return (
    <>
      <BackgroundLayers preset={preset} />
      <BrandPanel preset={preset} compact={compact} />
    </>
  );
}

// ───────────────────────── المكوّن الرئيسي ─────────────────────────
export default function AuthBackground({ children, dir }) {
  const [presetKey, setPresetKey] = useState(resolvePreset);
  const [layout, setLayout] = useState(resolveLayout);
  const [anchor, setAnchor] = useState(null);
  const isWide = useMediaQuery('(min-width:900px)');

  const preset = BACKGROUND_PRESETS[presetKey] || BACKGROUND_PRESETS.aurora;
  const split = layout === 'split' && isWide;
  const framed = layout === 'framed';

  const save = (key, value) => { try { localStorage.setItem(key, value); } catch { /* تجاهل */ } };
  const choosePreset = (k) => { setPresetKey(k); save(PRESET_KEY, k); setAnchor(null); };
  const chooseLayout = (l) => { setLayout(l); save(LAYOUT_KEY, l); setAnchor(null); };

  // في التخطيط المقسوم البطاقة على خلفية فاتحة: ظل أخف وإطار رفيع بدل الظل الداكن
  const lightCardSx = {
    '& .MuiContainer-root > .MuiPaper-root': {
      boxShadow: '0 24px 60px -28px rgba(15,23,42,.28) !important',
      border: '1px solid #e8ebf2',
    },
  };

  const switcherDark = split || framed ? false : preset.dark;

  const switcher = LOGIN_BACKGROUND.allowSwitcher && (
    <>
      <Tooltip title="تغيير المظهر">
        <IconButton
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            position: 'fixed', bottom: 16, insetInlineStart: 16, zIndex: 3,
            color: switcherDark ? '#fff' : '#334155',
            bgcolor: switcherDark ? 'rgba(255,255,255,.10)' : '#fff',
            border: `1px solid ${switcherDark ? 'rgba(255,255,255,.16)' : '#e2e8f0'}`,
            backdropFilter: 'blur(8px)',
            boxShadow: switcherDark ? 'none' : '0 4px 14px rgba(15,23,42,.08)',
            '&:hover': { bgcolor: switcherDark ? 'rgba(255,255,255,.18)' : '#f8fafc' },
          }}
        >
          <PaletteOutlined fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: 10000 }}
        PaperProps={{ sx: { borderRadius: '14px', minWidth: 230, py: 0.5 } }}
      >
        <ListSubheader sx={{ lineHeight: '32px', fontSize: 12, fontWeight: 800 }}>التخطيط</ListSubheader>
        {[
          ['framed', 'إطار مع لوحة دعائية', <ViewQuiltOutlined fontSize="small" />],
          ['split', 'نصف الشاشة', <VerticalSplitOutlined fontSize="small" />],
          ['center', 'في المنتصف', <CropSquareOutlined fontSize="small" />],
        ].map(([key, label, icon]) => (
          <MenuItem key={key} onClick={() => chooseLayout(key)} sx={{ gap: 1.5, py: 0.9 }}>
            <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
            <Typography fontSize={14} fontWeight={600} sx={{ flex: 1 }}>{label}</Typography>
            {key === layout && <Check fontSize="small" color="primary" />}
          </MenuItem>
        ))}

        <Divider sx={{ my: 0.5 }} />
        <ListSubheader sx={{ lineHeight: '32px', fontSize: 12, fontWeight: 800 }}>الخلفية</ListSubheader>
        {Object.entries(BACKGROUND_PRESETS).map(([key, p]) => (
          <MenuItem key={key} onClick={() => choosePreset(key)} sx={{ gap: 1.5, py: 0.9 }}>
            <Box sx={{
              width: 26, height: 26, borderRadius: '8px', flexShrink: 0,
              background: p.image ? `url("${LOGIN_BACKGROUND.imageUrl}") center/cover, ${p.base}` : p.base,
              border: '1px solid rgba(15,23,42,.12)',
            }} />
            <Typography fontSize={14} fontWeight={600} sx={{ flex: 1 }}>{p.label}</Typography>
            {key === presetKey && <Check fontSize="small" color="primary" />}
          </MenuItem>
        ))}
      </Menu>
    </>
  );

  // ── التخطيط المؤطّر: صفحة فاتحة + إطار يجمع البطاقة واللوحة الدعائية ──
  if (framed) {
    const brandName = LOGIN_BACKGROUND.brand?.name;
    return (
      <Box dir={dir} sx={{
        position: 'fixed', inset: 0, zIndex: 9999, overflowY: 'auto',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        px: 2, py: { xs: 3, md: 5 },
        bgcolor: '#f3f4f8',
        backgroundImage: 'radial-gradient(1000px 500px at 50% 0%, rgba(99,102,241,.07), transparent 70%)',
      }}>
        <Box sx={{
          my: 'auto', width: '100%', maxWidth: { md: 1080 },
          display: 'grid', gap: { md: 2.5 },
          gridTemplateColumns: { xs: '1fr', md: 'auto minmax(0, 1fr)' },
          alignItems: 'stretch',
        }}>
          {/* البطاقة — تأخذ ارتفاع الصف كاملًا لتتساوى مع اللوحة */}
          <Box sx={{
            display: 'flex', justifyContent: 'center',
            '& .MuiContainer-root': { height: { md: '100%' }, px: { md: '0 !important' } },
            // عرض ثابت للبطاقة حسب نوعها: الدخول والاستعادة أضيق، والتسجيل أعرض
            '& .MuiContainer-maxWidthXs': { width: { md: 430 } },
            '& .MuiContainer-maxWidthSm': { width: { md: 540 } },
            '& .MuiContainer-root > .MuiPaper-root': {
              height: { md: '100%' }, justifyContent: { md: 'center' },
              borderRadius: '20px !important',
              boxShadow: '0 1px 2px rgba(15,23,42,.04), 0 12px 32px -18px rgba(15,23,42,.18) !important',
              border: '1px solid #e8ebf2',
            },
          }}>
            {children}
          </Box>

          {/* اللوحة الدعائية */}
          {isWide && (
            <Box sx={{
              position: 'relative', overflow: 'hidden', borderRadius: '20px', minHeight: 520,
              boxShadow: '0 12px 32px -18px rgba(15,23,42,.35)',
            }}>
              <Banner preset={preset} compact />
            </Box>
          )}
        </Box>

        {brandName && (
          <Typography sx={{ mt: 3, fontSize: 13, color: '#94a3b8', fontWeight: 500 }}>
            © {new Date().getFullYear()} {brandName}
          </Typography>
        )}

        {switcher}
      </Box>
    );
  }

  // ── التخطيط المقسوم ──
  if (split) {
    return (
      <Box dir={dir} sx={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'grid', gridTemplateColumns: { md: '1fr 1fr', lg: '5fr 6fr' },
      }}>
        {/* النموذج */}
        <Box sx={{
          position: 'relative', overflowY: 'auto', display: 'flex', flexDirection: 'column',
          px: 3, py: 4, bgcolor: '#f6f7fb',
          backgroundImage: 'radial-gradient(rgba(15,23,42,.06) 1px, transparent 1.4px)',
          backgroundSize: '22px 22px',
          ...lightCardSx,
        }}>
          <Box sx={{ my: 'auto', width: '100%', display: 'flex', justifyContent: 'center' }}>
            {children}
          </Box>
        </Box>

        {/* لوحة الهوية */}
        <Box sx={{ position: 'relative', overflow: 'hidden' }}>
          <Banner preset={preset} />
        </Box>

        {switcher}
      </Box>
    );
  }

  // ── التخطيط الوسطي (وعلى الجوال دائمًا) ──
  return (
    <Box dir={dir} sx={{
      position: 'fixed', inset: 0, zIndex: 9999,
      overflowY: 'auto', overflowX: 'hidden',
      display: 'flex', flexDirection: 'column', px: 2, py: { xs: 3, md: 4 },
    }}>
      <BackgroundLayers preset={preset} spotlight fixed />
      <Box sx={{ position: 'relative', zIndex: 1, my: 'auto', width: '100%', display: 'flex', justifyContent: 'center' }}>
        {children}
      </Box>
      {switcher}
    </Box>
  );
}